/**
 * BulkUserUploadModal — Two-step CSV bulk user creation flow.
 * Step 1: Upload CSV  →  Step 2: Preview & confirm  →  Step 3: Result + credentials download
 *
 * Credentials (email + temporaryPassword) are held in component state only for the
 * duration of the result screen. They are cleared on modal close and never logged.
 */

import { useRef, useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { UploadCloud, CheckCircle, XCircle, Download, AlertTriangle } from 'lucide-react';
import styled from 'styled-components';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import userService from '../../services/api/userService';
import { useAuthStore } from '../../stores/authStore';
import { useIsSuperAdmin } from '../../hooks/useIsSuperAdmin';
import type {
  BulkUserRow,
  BulkUserValidateResponse,
  BulkUserExecuteResponse,
} from '../../services/api/userService';
import {
  DropZone,
  DropLabel,
  DropSubLabel,
  FileNameChip,
  SummaryBar,
  ValidCount,
  InvalidCount,
  TotalCount,
  TableWrapper,
  Table,
  Th,
  Td,
  Tr,
  StatusIcon,
  ErrorBanner,
  ResultBanner,
  ResultIcon,
  ResultTitle,
  ResultSubtext,
  ModalFooter,
  FooterRight,
  LoadingState,
} from '../shared/BulkUploadShared';
import { downloadCredentialsCsv, downloadErrorsCsv, downloadTemplateCsv } from '../shared/bulkUploadUtils';

// ============================================================================
// Constants
// ============================================================================

const TEMPLATE_HEADERS = ['email', 'firstName', 'lastName', 'role', 'activateLogin'];
const TEMPLATE_SAMPLE_ROWS = [
  { email: 'alex.rivera@example.com', firstName: 'Alex', lastName: 'Rivera', role: 'RBT', activateLogin: 'true' },
  { email: 'morgan.lee@example.com', firstName: 'Morgan', lastName: 'Lee', role: 'BCBA', activateLogin: 'false' },
];
// Must match the API's FileInterceptor limit (2 MB)
const MAX_FILE_SIZE = 2 * 1024 * 1024;

// ============================================================================
// Helpers
// ============================================================================

const downloadErrors = (rows: BulkUserRow[]) => {
  downloadErrorsCsv(
    rows as Parameters<typeof downloadErrorsCsv>[0],
    ['email', 'firstName', 'lastName', 'role', 'activateLogin'],
    'user-upload-errors.csv',
  );
};

// ============================================================================
// Styled Components
// ============================================================================

const UploadActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

const CredentialsNote = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.warning.text};
  background: ${({ theme }) => theme.colors.status.warning.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.warning.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[3]};
  margin-bottom: 0;
`;

// ============================================================================
// Types
// ============================================================================

type Step = 'upload' | 'preview' | 'result';

interface BulkUserUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ============================================================================
// Component
// ============================================================================

export const BulkUserUploadModal = ({ isOpen, onClose }: BulkUserUploadModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = useIsSuperAdmin();

  // For super admins the API requires organizationId as a query param — derive it from their session
  const orgId = isSuperAdmin && user?.organizationId ? user.organizationId : undefined;

  // Cycle ref: incremented on every close so stale in-flight callbacks are silently dropped
  const openCycleRef = useRef(0);

  const [step, setStep] = useState<Step>('upload');
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validateResult, setValidateResult] = useState<BulkUserValidateResponse | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [executeResult, setExecuteResult] = useState<BulkUserExecuteResponse | null>(null);

  const validateMutation = useMutation<
    { result: BulkUserValidateResponse; cycle: number },
    Error,
    File,
    { cycle: number }
  >({
    onMutate: () => ({ cycle: openCycleRef.current }),
    mutationFn: (file: File) =>
      userService.bulkValidateUsers(file, orgId).then((result) => ({ result, cycle: openCycleRef.current })),
    onSuccess: ({ result, cycle }) => {
      // Discard callbacks from a previous open cycle
      if (cycle !== openCycleRef.current) return;
      setValidateResult(result);
      setStep('preview');
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.cycle !== openCycleRef.current) return;
      setUploadError(t('bulkUserUpload.validateError'));
    },
  });

  const executeMutation = useMutation<
    { result: BulkUserExecuteResponse; cycle: number },
    Error,
    void,
    { cycle: number }
  >({
    onMutate: () => ({ cycle: openCycleRef.current }),
    mutationFn: () => {
      const validRows = (validateResult?.rows ?? []).filter((r) => r.valid);
      return userService.bulkExecuteUsers(validRows, orgId).then((result) => ({ result, cycle: openCycleRef.current }));
    },
    onSuccess: ({ result, cycle }) => {
      // Discard callbacks from a previous open cycle
      if (cycle !== openCycleRef.current) return;
      queryClient.invalidateQueries({ queryKey: ['members'] });
      setExecuteResult(result);
      setStep('result');
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.cycle !== openCycleRef.current) return;
      setUploadError(t('bulkUserUpload.executeError'));
    },
  });

  const handleFileSelect = useCallback((file: File) => {
    if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
      setUploadError(t('bulkUserUpload.fileTypeError'));
      // Clear previously-selected file so the Upload button does not submit stale content
      setSelectedFile(null);
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setUploadError(t('bulkUserUpload.fileSizeError'));
      // Clear previously-selected file so the Upload button does not submit stale content
      setSelectedFile(null);
      return;
    }
    setUploadError(null);
    setSelectedFile(file);
  }, [t]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleClose = () => {
    // Bump cycle first so any in-flight callback arriving after this point is silently dropped
    openCycleRef.current += 1;
    validateMutation.reset();
    executeMutation.reset();
    setStep('upload');
    setSelectedFile(null);
    setValidateResult(null);
    setUploadError(null);
    // Clear credentials from state on close
    setExecuteResult(null);
    onClose();
  };

  const handleDownloadTemplate = () => {
    downloadTemplateCsv(TEMPLATE_HEADERS, TEMPLATE_SAMPLE_ROWS, 'user-upload-template.csv');
  };

  const rows = validateResult?.rows ?? [];
  const validCount = rows.filter((r) => r.valid).length;
  const invalidCount = rows.filter((r) => !r.valid).length;

  const TITLE_MAP: Record<Step, string> = {
    upload: t('bulkUserUpload.titleUpload'),
    preview: t('bulkUserUpload.titlePreview'),
    result: t('bulkUserUpload.titleResult'),
  };

  const createButtonLabel = t('bulkUserUpload.createButton', { count: validCount });

  const buildResultSubtext = (result: BulkUserExecuteResponse) => {
    const skippedPart = result.skipped > 0
      ? t('bulkUserUpload.resultSkipped', { count: result.skipped })
      : '';
    return t('bulkUserUpload.resultSubtext_other', { count: result.created, skipped: skippedPart });
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={TITLE_MAP[step]} size="lg">
      {/* ── Step 1: Upload ── */}
      {step === 'upload' && (
        <>
          {uploadError && (
            <ErrorBanner role="alert">
              <AlertTriangle />
              {uploadError}
            </ErrorBanner>
          )}

          <DropZone
            $dragOver={dragOver}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            role="button"
            tabIndex={0}
            aria-label={t('bulkUserUpload.dropLabel')}
            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
          >
            <UploadCloud />
            <DropLabel>{t('bulkUserUpload.dropLabel')}</DropLabel>
            <DropSubLabel>{t('bulkUserUpload.dropSubLabel')}</DropSubLabel>
            {selectedFile && <FileNameChip>{selectedFile.name}</FileNameChip>}
          </DropZone>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
              e.target.value = '';
            }}
          />

          <UploadActions>
            <Button variant="ghost" leftIcon={<Download />} onClick={handleDownloadTemplate}>
              {t('bulkUserUpload.downloadSample')}
            </Button>
          </UploadActions>

          {validateMutation.isPending && (
            <LoadingState>{t('bulkUserUpload.validatingLabel')}</LoadingState>
          )}

          <ModalFooter>
            <Button variant="secondary" onClick={handleClose}>
              {t('bulkUserUpload.cancelButton')}
            </Button>
            <FooterRight>
              <Button
                variant="primary"
                onClick={() => selectedFile && validateMutation.mutate(selectedFile)}
                disabled={!selectedFile || validateMutation.isPending}
                isLoading={validateMutation.isPending}
              >
                {t('bulkUserUpload.uploadAndValidate')}
              </Button>
            </FooterRight>
          </ModalFooter>
        </>
      )}

      {/* ── Step 2: Preview ── */}
      {step === 'preview' && validateResult && (
        <>
          {uploadError && (
            <ErrorBanner role="alert">
              <AlertTriangle />
              {uploadError}
            </ErrorBanner>
          )}

          <SummaryBar>
            <ValidCount>
              <CheckCircle />
              {t('bulkUserUpload.validLabel', { count: validCount })}
            </ValidCount>
            <InvalidCount>
              <XCircle />
              {t('bulkUserUpload.invalidLabel', { count: invalidCount })}
            </InvalidCount>
            <TotalCount>{t('bulkUserUpload.totalLabel', { total: rows.length })}</TotalCount>
          </SummaryBar>

          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>{t('bulkUserUpload.colRow')}</Th>
                  <Th>{t('bulkUserUpload.colEmail')}</Th>
                  <Th>{t('bulkUserUpload.colFirstName')}</Th>
                  <Th>{t('bulkUserUpload.colLastName')}</Th>
                  <Th>{t('bulkUserUpload.colRole')}</Th>
                  <Th>Login Access</Th>
                  <Th>{t('bulkUserUpload.colStatus')}</Th>
                  <Th>{t('bulkUserUpload.colReason')}</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <Tr key={row.row} $invalid={!row.valid}>
                    <Td $invalid={!row.valid}>{row.row}</Td>
                    <Td $invalid={!row.valid}>{row.email ?? '—'}</Td>
                    <Td $invalid={!row.valid}>{row.firstName ?? '—'}</Td>
                    <Td $invalid={!row.valid}>{row.lastName ?? '—'}</Td>
                    <Td $invalid={!row.valid}>{row.role ?? '—'}</Td>
                    <Td $invalid={!row.valid}>
                      {row.activateLogin === true ? 'Yes' : row.activateLogin === false ? 'No' : '—'}
                    </Td>
                    <Td>
                      <StatusIcon $valid={row.valid}>
                        {row.valid ? <CheckCircle /> : <XCircle />}
                      </StatusIcon>
                    </Td>
                    <Td $invalid={!row.valid}>{row.reason ?? '—'}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>

          <ModalFooter>
            <Button variant="secondary" onClick={() => setStep('upload')}>
              {t('bulkUserUpload.backButton')}
            </Button>
            <FooterRight>
              {invalidCount > 0 && (
                <Button
                  variant="ghost"
                  leftIcon={<Download />}
                  onClick={() => downloadErrors(rows)}
                >
                  {t('bulkUserUpload.downloadErrors')}
                </Button>
              )}
              <Button
                variant="primary"
                onClick={() => executeMutation.mutate()}
                disabled={validCount === 0 || executeMutation.isPending}
                isLoading={executeMutation.isPending}
              >
                {createButtonLabel}
              </Button>
            </FooterRight>
          </ModalFooter>
        </>
      )}

      {/* ── Step 3: Result ── */}
      {step === 'result' && executeResult && (
        <>
          <ResultBanner>
            <ResultIcon>
              <CheckCircle />
            </ResultIcon>
            <ResultTitle>{t('bulkUserUpload.resultTitle')}</ResultTitle>
            <ResultSubtext>{buildResultSubtext(executeResult)}</ResultSubtext>
          </ResultBanner>

          {executeResult.credentials.length > 0 && (
            <CredentialsNote>{t('bulkUserUpload.credentialsNote')}</CredentialsNote>
          )}

          {executeResult.credentials.some((c) => !c.entraProvisioned) && (
            <ErrorBanner role="alert">
              <AlertTriangle />
              {t('bulkUserUpload.entraProvisioningWarning')}
            </ErrorBanner>
          )}

          <ModalFooter>
            <div />
            <FooterRight>
              {executeResult.credentials.length > 0 && (
                <Button
                  variant="secondary"
                  leftIcon={<Download />}
                  onClick={() => downloadCredentialsCsv(executeResult.credentials)}
                >
                  {t('bulkUserUpload.downloadCredentials')}
                </Button>
              )}
              <Button variant="primary" onClick={handleClose}>
                {t('bulkUserUpload.doneButton')}
              </Button>
            </FooterRight>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
};

export default BulkUserUploadModal;
